import { useCallback } from 'react';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { validateDiscountCode } from '@app/service/eshop/checkout/actions/validateDiscountCode';
import { ProductCode } from '@app/domain/model/eshop/transport/ProductCode';
import { recalculatePremiumsForPaymentFrequency } from '@app/service/eshop/transport/actions/recalculatePremiumsForPaymentFrequency';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { logger } from '@app/infrastructure/logger/logger';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { TransportInsuranceRequestResult } from '@app/service/eshop/transport/actions/requestTransportInsurance';

export function useTransportWizardCallbacks(
    discountCodeData: DiscountCodeData,
    setDiscountCodeData: (data: DiscountCodeData) => void,
    requestResult: TransportInsuranceRequestResult,
    setSelectedPaymentMethodType: (type: Nullable<string>) => void,
    setCurrentStepKeyRef: React.MutableRefObject<((key: string) => void) | null>,
    formState: FormState<PurchaseTransportInsuranceResult>,
    insurancePackage: PremiumType,
    setIsCalculatingPremiums: (calculating: boolean) => void,
    setLoaderText: (text: string) => void,
    setCurrentPremiums: (premiums: TransportPremiumsResponse[]) => void,
    setPaymentFrequency: (frequency: number) => void,
    t: (key: string) => string,
) {
    const handleDiscountCode = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setDiscountCodeData({
                ...discountCodeData,
                discountCode: e.target.value,
                isInvalid: false,
                isSuccess: false,
                validEcaPapCode: undefined,
                validCascoCode: undefined,
            });
        },
        [discountCodeData, setDiscountCodeData],
    );

    const handleApplyDiscountClick = useCallback(async () => {
        const [ecaPapResult, cascoResult] = await Promise.all([
            validateDiscountCode(discountCodeData.discountCode, ProductCode.EcaPap, requestResult.personalCode),
            validateDiscountCode(discountCodeData.discountCode, ProductCode.Casco, requestResult.personalCode),
        ]);

        const isEcaPapValid = ecaPapResult?.isValid === true;
        const isCascoValid = cascoResult?.isValid === true;

        if (isEcaPapValid || isCascoValid) {
            setDiscountCodeData({
                ...discountCodeData,
                isSuccess: true,
                isInvalid: false,
                validEcaPapCode: isEcaPapValid ? discountCodeData.discountCode : undefined,
                validCascoCode: isCascoValid ? discountCodeData.discountCode : undefined,
            });
        } else {
            setDiscountCodeData({
                ...discountCodeData,
                isInvalid: true,
                isSuccess: false,
                validEcaPapCode: undefined,
                validCascoCode: undefined,
            });
        }
    }, [discountCodeData, setDiscountCodeData, requestResult.personalCode]);

    const handlePaymentMethodTypeChange = useCallback(
        (paymentMethodType: Nullable<string>) => {
            setSelectedPaymentMethodType(paymentMethodType);
        },
        [setSelectedPaymentMethodType],
    );

    const onSuccessfulLogin = useCallback(() => {
        setCurrentStepKeyRef.current?.('step-2-6');
    }, [setCurrentStepKeyRef]);

    const navigateToStep = useCallback(
        (stepKey: string) => {
            setCurrentStepKeyRef.current?.(stepKey);
        },
        [setCurrentStepKeyRef],
    );

    const handlePaymentFrequencyChange = useCallback(
        async (paymentFrequency: number) => {
            setPaymentFrequency(paymentFrequency);

            if (!formState.data?.formData) return;

            setIsCalculatingPremiums(true);
            setLoaderText(t('eshop.transport.calculatingPremiums'));

            try {
                const shouldCalculateCasco =
                    insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl;

                const result = await recalculatePremiumsForPaymentFrequency(
                    formState.data.formData,
                    requestResult.vehicleInformation,
                    paymentFrequency.toString(),
                    shouldCalculateCasco,
                );

                if (result.premiums.length > 0) {
                    setCurrentPremiums(result.premiums);
                }
            } catch (error) {
                logger.logException(error);
            } finally {
                setIsCalculatingPremiums(false);
                setLoaderText('');
            }
        },
        [
            formState.data?.formData,
            requestResult.vehicleInformation,
            insurancePackage,
            setIsCalculatingPremiums,
            setLoaderText,
            setCurrentPremiums,
            setPaymentFrequency,
            t,
        ],
    );

    return {
        handleDiscountCode,
        handleApplyDiscountClick,
        handlePaymentMethodTypeChange,
        onSuccessfulLogin,
        navigateToStep,
        handlePaymentFrequencyChange,
    };
}
